<template>
  <b-sidebar
    id="sidebar"
    :visible="isSidebarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    no-close-on-backdrop
    right
    @shown="loadData"
    @hidden="resetForm"
    @change="(val) => $emit('update:is-sidebar-active', val)"
  >
    <template #default="{ hide }">
      <!-- Header -->
      <div
        class="
          d-flex
          justify-content-between
          align-items-center
          content-sidebar-header
          px-2
          py-1
        "
      >
        <h5 class="mb-0 text-capitalize">
          {{ isAdd ? `${$t("actions.add")} ${$t("ntechlab.master_account.title")}` :`${$t("actions.edit")} ${$t("ntechlab.master_account.title")}` }}
        </h5>

        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>

      <!-- BODY -->
      <validation-observer #default="{ handleSubmit }" ref="refFormObserver">
        <!-- Form -->
        <b-form
          class="p-2"
          @submit.prevent="handleSubmit(onSubmit)"
          @reset.prevent="resetForm"
        >
          <!-- Name -->
          <validation-provider
            #default="validationContext"
            name="name"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('ntechlab.master_account.name')"
              label-for="name"
            >
              <b-form-input
                id="name"
                v-model="itemData.name"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Comment -->
          <validation-provider
            #default="validationContext"
            name="comment"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('ntechlab.watch_list.comment')"
              label-for="comment"
            >
              <b-form-input
                id="comment"
                v-model="itemData.comment"
                :state="getValidationState(validationContext)"
                trim
              />

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <!-- Color -->
          <validation-provider
            #default="validationContext"
            name="color"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('ntechlab.watch_list.color')"
              label-for="color"
            >
              <div class="d-flex align-items-center" style="position: relative;">
                <!-- Color square preview -->
                <div
                  :style="{
                    width: '32px',
                    height: '32px',
                    backgroundColor: itemData.color ? '#' + itemData.color : '#ccc',
                    border: '1px solid #888',
                    borderRadius: '4px',
                    marginRight: '10px',
                    flexShrink: 0
                  }"
                ></div>
                <b-form-input
                  id="color"
                  v-model="itemData.color"
                  :state="getValidationState(validationContext)"
                  readonly
                  class="mr-1"
                  placeholder="Select color"
                  @click="showColorPicker = !showColorPicker"
                  :style="{ cursor: 'pointer' }"
                />
                <b-button variant="secondary" @click="showColorPicker = !showColorPicker" class="ml-1">
                  Select Color
                </b-button>
                <div
                  v-if="showColorPicker"
                  style="position: absolute; z-index: 10; top: 110%; left: 0; border-radius: 8px; box-shadow: 0 2px 8px #0008; padding: 16px; min-width: 180px; background-color: #b8b8b8"
                >
                  <input
                    type="color"
                    v-model="tempColor"
                    style="width: 100%; height: 40px; border: none; background: none;"
                  />
                  <div class="d-flex justify-content-end mt-2">
                    <b-button size="sm" variant="primary" @click="selectColor">Select</b-button>
                    <b-button size="sm" variant="outline-secondary" class="ml-2" @click="showColorPicker = false">Cancel</b-button>
                  </div>
                </div>
              </div>

              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
          <!-- Master Account -->
          <validation-provider
            #default="validationContext"
            name="master_account_id"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('ntechlab.watch_list.master_account')"
              label-for="master_account_id"
            >
              <v-select
                id="master_account_id"
                v-model="itemData.id_master_account"
                :options="masterAccountOptions"
                :state="getValidationState(validationContext)"
                placeholder="Select Master Account"
                label="name"
                :reduce="option => option.id"
              />
            </b-form-group>
          </validation-provider>

          <!-- Default Configuration -->
          <validation-provider
            #default="validationContext"
            name="default_configuration"
            rules="required"
          >
            <b-form-group
              class="text-capitalize"
              :label="$t('ntechlab.watch_list.default_configuration')"
              label-for="default_configuration"
            >
              <b-form-checkbox
                id="default_configuration"
                v-model="itemData.default_configuration"
                name="default_configuration"
                value="1"
                class="my-1 text-capitalize"
                unchecked-value="0"
                switch
                >
                {{ $t('ntechlab.watch_list.default_configuration') }}
                </b-form-checkbox>
              <b-form-invalid-feedback>
                {{ validationContext.errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>

          <b-card
            v-if="itemData.default_configuration === '0' || itemData.default_configuration === 0"
            class="mt-2"
          >
            <b-row>
              <b-col cols="12" md="6">
                <b-form-checkbox v-model="itemData.notify" class="mb-2">
                  Notify
                </b-form-checkbox>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-checkbox v-model="itemData.acknowledge" class="mb-2">
                  Acknowledge
                </b-form-checkbox>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group label="Camera Groups" label-for="camera_groups" class="mb-2">
                  <b-form-input
                    id="camera_groups"
                    v-model="itemData.camera_groups"
                    placeholder="Camera Groups"
                  />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group label="Face Threshold" label-for="face_threshold" class="mb-2">
                  <b-form-input
                    id="face_threshold"
                    v-model="itemData.face_threshold"
                    type="number"
                    placeholder="Face Threshold"
                  />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group label="Body Threshold" label-for="body_threshold" class="mb-2">
                  <b-form-input
                    id="body_threshold"
                    v-model="itemData.body_threshold"
                    type="number"
                    placeholder="Body Threshold"
                  />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group label="Car Threshold" label-for="car_threshold" class="mb-2">
                  <b-form-input
                    id="car_threshold"
                    v-model="itemData.car_threshold"
                    type="number"
                    placeholder="Car Threshold"
                  />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-checkbox v-model="itemData.ignore_events" class="mb-2">
                  Ignore Events
                </b-form-checkbox>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-checkbox v-model="itemData.send_events_to_external_vms" class="mb-2">
                  Send Events to External VMS
                </b-form-checkbox>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group label="Active After" label-for="active_after" class="mb-2">
                  <b-form-input
                    id="active_after"
                    v-model="itemData.active_after"
                    placeholder="Active After"
                  />
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6">
                <b-form-group label="Active Before" label-for="active_before" class="mb-2">
                  <b-form-input
                    id="active_before"
                    v-model="itemData.active_before"
                    placeholder="Active Before"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </b-card>

          <!-- Schedule Disable Active -->
          <b-form-group
            class="text-capitalize mt-2"
            :label="$t('ntechlab.watch_list.title_schedule_disable')"
            label-for="disable_schedule_active"
          >
            <b-form-checkbox
              id="disable_schedule_active"
              v-model="itemData.disable_schedule_active"
              name="disable_schedule_active"
              switch
              class="my-1 text-capitalize"
            >
              {{ $t('ntechlab.watch_list.enable_schedule_disable') }}
            </b-form-checkbox>
          </b-form-group>

          <b-card
            v-if="itemData.disable_schedule_active"
            class="mt-2"
          >
            <b-row>
              <b-col cols="12" md="6" v-for="day in ['monday','tuesday','wednesday','thursday','friday','saturday','sunday']" :key="day">
                <b-form-group :label="day.charAt(0).toUpperCase() + day.slice(1)" :label-for="'disable_schedule_' + day" class="mb-2">
                  <b-form-input
                    :id="'disable_schedule_' + day"
                    v-model="itemData.disable_schedule[day]"
                    :placeholder="day.charAt(0).toUpperCase() + day.slice(1)"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </b-card>

          <!-- Form Actions -->
          <div class="d-flex mt-2">
            <b-button
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              variant="primary"
              class="mr-2 text-capitalize"
              type="submit"
            >
              {{ $t("actions.save") }}
            </b-button>
            <b-button
              v-ripple.400="'rgba(186, 191, 199, 0.15)'"
              type="button"
              variant="outline-secondary"
              @click="hide"
              class="text-capitalize"
            >
             {{$t('actions.cancel')}}
            </b-button>
          </div>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar,
  BForm,
  BFormGroup,
  BFormInput,
  BFormInvalidFeedback,
  BButton,
  BFormCheckbox,
} from "bootstrap-vue";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import vSelect from "vue-select";
import { ref, watch } from "@vue/composition-api";
import { required, alphaNum, email, positive, required_if } from "@validations";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import formValidation from "@core/comp-functions/forms/form-validation";
import Ripple from "vue-ripple-directive";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { tr } from "date-fns/locale";

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BFormInvalidFeedback,
    BButton,
    vSelect,
    BFormCheckbox,
    ValidationProvider,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  model: {
    prop: "isSidebarActive",
    event: "update:is-sidebar-active",
  },
  props: {
    isSidebarActive: {
      type: Boolean,
      required: true,
    },
    itemEdit: {
      type: Object,
      required: false,
    },
    tenantOptions: {
      type: Array,
      required: true,
    },
    isAdd: {
      type: Boolean,
      required: true,
    },
    masterAccountOptions: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      required,
      alphaNum,
      email,
      positive,
    };
  },
  setup(props, { emit }) {

    const tenantArray = ref([]);
    const { t } = useI18nUtils();
    const blankData = {
      name: null,
      description: null,
      api_user: null,
      api_password: null,
      api_server: null,
      tenants: null,
      default_configuration: props.isAdd ? 1 : 0,
      notify: false,
      acknowledge: false,
      camera_groups: null,
      face_threshold: null,
      body_threshold: null,
      car_threshold: null,
      ignore_events: false,
      send_events_to_external_vms: false,
      active_after: null,
      active_before: null,
      disable_schedule_active: props.isAdd ? true : false,
      master_account_id: null,
      disable_schedule: {
        monday: null,
        tuesday: null,
        wednesday: null,
        thursday: null,
        friday: null,
        saturday: null,
        sunday: null,
      },

    };

    const toast = useToast();

    const itemData = ref(JSON.parse(JSON.stringify(blankData)));
    const resetData = () => {
      itemData.value = JSON.parse(JSON.stringify(blankData));
      itemData.value.default_configuration = props.isAdd ? 1 : 0;
      itemData.value.id_master_account = props.itemEdit.master_account_id;
    };
    console.log(props.itemEdit);

    const onSubmit = () => {
      let dispatch;

      if (props.isAdd) {
        itemData.value.tenants = tenantArray;
        dispatch = store.dispatch("ntechlab_watch_list/add", itemData.value);
      } else {
        const req = {
          id: itemData.value.id,
          params: {
            name: itemData.value.name,
            comment: itemData.value.comment,
            color: itemData.value.color,
            id_master_account: itemData.value.id_master_account,
            default_configuration: itemData.value.default_configuration,
            notify: itemData.value.notify,
            acknowledge: itemData.value.acknowledge,
            camera_groups: itemData.value.camera_groups,
            face_threshold: itemData.value.face_threshold,
            body_threshold: itemData.value.body_threshold,
            car_threshold: itemData.value.car_threshold,
            ignore_events: itemData.value.ignore_events,
            send_events_to_external_vms: itemData.value.send_events_to_external_vms,
            active_after: itemData.value.active_after,
            active_before: itemData.value.active_before,
            disable_schedule_active: itemData.value.disable_schedule_active,
            disable_schedule: itemData.value.disable_schedule,
          },
        };

        dispatch = store.dispatch("ntechlab_watch_list/edit", req);
      }

      dispatch
        .then((response) => {
          if (response.success) {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "CheckIcon",
                variant: "success",
              },
            });
            emit("refetch-data");
            emit("update:is-sidebar-active", false);
          } else {
            toast({
              component: ToastificationContent,
              props: {
                title: response.message,
                icon: "AlertTriangleIcon",
                variant: "danger",
              },
            });
          }
        })
        .catch((response) => {
          toast({
            component: ToastificationContent,
            props: {
              title: response.response.data.message,
              icon: "AlertTriangleIcon",
              variant: "danger",
            },
          });
        });
    };

    const { refFormObserver, getValidationState, resetForm } =
      formValidation(resetData);

    const loadData = () => {
      tenantArray.value = [];
      if (!props.isAdd) {
        itemData.value = props.itemEdit;
        tenantArray.value = props.itemEdit.tenants.map(tenant => (tenant.id));
      } else {
        resetData();
      }
    };

    const showColorPicker = ref(false);
    const tempColor = ref('#' + (itemData.value.color || '000000'));

    const selectColor = () => {
      itemData.value.color = tempColor.value.replace('#', '');
      showColorPicker.value = false;
    };

    watch(() => itemData.value.default_configuration, (val) => {
      if (val) {
        itemData.value.notify = false;
        itemData.value.acknowledge = false;
        itemData.value.camera_groups = null;
        itemData.value.face_threshold = null;
        itemData.value.body_threshold = null;
        itemData.value.car_threshold = null;
        itemData.value.ignore_events = false;
        itemData.value.send_events_to_external_vms = false;
        itemData.value.active_after = null;
        itemData.value.active_before = null;
      }
    });

    return {
      itemData,
      onSubmit,
      tenantArray,
      refFormObserver,
      getValidationState,
      loadData,
      resetForm,
      showColorPicker,
      tempColor,
      selectColor,
    };
  },
};
</script>